import React from 'react';
import { ShoppingCart, Plus, Minus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/hooks/useCart';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { SheetClose } from '@/components/ui/sheet';
import { formatCurrency } from '@/lib/currency';
import CartAddonsStrip from '@/components/CartAddonsStrip';

interface CartDrawerProps {
  trigger?: React.ReactNode;
  language?: 'en' | 'ar';
}

const CartDrawer = ({ trigger, language = 'en' }: CartDrawerProps) => {
  const { state, removeItem, updateQuantity } = useCart();
  const { navigateWithCity } = useCityNavigation();

  const translations = {
    en: {
      title: "Shopping Cart",
      description: "Review your selected flowers before checkout",
      emptyCart: "Your cart is empty",
      emptyCartSubtext: "Add some beautiful flowers to get started",
      each: "each",
      total: "Total:",
      proceedToCheckout: "Proceed to Checkout",
      items: "items"
    },
    ar: {
      title: "سلة التسوق",
      description: "راجع الورد المختارة قبل إتمام الطلب",
      emptyCart: "سلة التسوق فارغة",
      emptyCartSubtext: "أضف بعض الورد الجميلة للبدء",
      each: "لكل قطعة",
      total: "المجموع:",
      proceedToCheckout: "إتمام الطلب",
      items: "عناصر"
    }
  };

  const t = translations[language];

  const handleCheckout = () => {
    navigateWithCity('/checkout');
  };

  return (
    <Sheet>
      {trigger && (
        <SheetTrigger asChild>
          {trigger}
        </SheetTrigger>
      )}
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className={`flex items-center gap-2 ${language === 'ar' ? 'text-right' : ''}`}>
            <ShoppingCart className="h-5 w-5" style={{ strokeWidth: 1.5 }} />
            {t.title} ({state.itemCount} {t.items})
          </SheetTitle>
          <SheetDescription className={language === 'ar' ? 'text-right' : ''}>
            {t.description}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {state.items.length === 0 ? (
            <div className={`text-center py-12 ${language === 'ar' ? 'text-right' : ''}`}>
              <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground mb-4" style={{ strokeWidth: 1.5 }} />
              <p className="text-muted-foreground">{t.emptyCart}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t.emptyCartSubtext}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {state.items.map((item) => (
                  <div key={item.id} className={`flex items-center gap-3 p-3 border rounded-lg ${language === 'ar' ? 'text-right' : ''}`}>
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{item.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(item.price, { language })} {t.each}
                      </p>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        
                        <span className="font-medium min-w-[2rem] text-center">
                          {item.quantity}
                        </span>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className={language === 'ar' ? 'text-left' : 'text-right'}>
                      <p className="font-medium">
                        {formatCurrency(item.price * item.quantity, { language })}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => removeItem(item.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <Separator />

              {/* Upsell: addons (balloons, cards, etc.) — only renders
                  if there are any addon products live in the current city. */}
              <CartAddonsStrip language={language} variant="drawer" />

              <div className={`space-y-3 ${language === 'ar' ? 'text-right' : ''}`}>
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>{t.total}</span>
                  <span>{formatCurrency(state.total, { language })}</span>
                </div>
                
                <SheetClose asChild>
                  <Button 
                    onClick={handleCheckout} 
                    className="w-full" 
                    size="lg"
                    disabled={state.items.length === 0}
                  >
                    {t.proceedToCheckout}
                  </Button>
                </SheetClose>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;